import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EquipmentAsset, EquipmentStatus, MaterialCategory } from '@models/warehouse.model';
import { generateEquipmentAssetsPdfFilename } from './pdfFilename';

interface EquipmentAssetsPdfOptions {
  equipment: EquipmentAsset[];
  categories: MaterialCategory[];
}

// Color constants (matching Tailwind theme)
const PRIMARY: [number, number, number] = [79, 70, 229]; // indigo-600
const HEADER_BG: [number, number, number] = [249, 250, 251]; // gray-50
const MAINTENANCE_BG: [number, number, number] = [255, 247, 237]; // orange-50
const TEXT_PRIMARY: [number, number, number] = [17, 24, 39]; // gray-900
const TEXT_SECONDARY: [number, number, number] = [107, 114, 128]; // gray-500
const GREEN: [number, number, number] = [22, 163, 74]; // green-600
const BLUE: [number, number, number] = [37, 99, 235]; // blue-600
const ORANGE: [number, number, number] = [234, 88, 12]; // orange-600
const PURPLE: [number, number, number] = [147, 51, 234]; // purple-600
const RED: [number, number, number] = [220, 38, 38]; // red-600

const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US').format(value);

export function generateEquipmentAssetsPdf({
  equipment,
  categories,
}: EquipmentAssetsPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return '-';
    return categories.find((c) => c.categoryId === categoryId)?.name || '-';
  };

  const getStatusLabel = (status: EquipmentStatus): string => {
    switch (status) {
      case EquipmentStatus.AVAILABLE: return 'Available';
      case EquipmentStatus.IN_USE: return 'In Use';
      case EquipmentStatus.MAINTENANCE: return 'Maintenance';
      case EquipmentStatus.RESERVED: return 'Reserved';
      case EquipmentStatus.RETIRED: return 'Retired';
      default: return '-';
    }
  };

  // Summary stats
  const totalAssets = equipment.length;
  const availableCount = equipment.filter((e) => e.status === EquipmentStatus.AVAILABLE).length;
  const inUseCount = equipment.filter((e) => e.status === EquipmentStatus.IN_USE).length;
  const maintenanceCount = equipment.filter((e) => e.status === EquipmentStatus.MAINTENANCE).length;

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // ========================
  // HEADER BAR
  // ========================
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Equipment Assets Report', margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  // ========================
  // SUMMARY STATS (4 cards)
  // ========================
  const statsY = 28;
  const cardGap = 4;
  const cardWidth = (pageWidth - margin * 2 - cardGap * 3) / 4;
  const cardHeight = 18;

  const stats = [
    { label: 'Total Assets', value: formatNumber(totalAssets), color: PRIMARY },
    { label: 'Available', value: formatNumber(availableCount), color: GREEN },
    { label: 'In Use', value: formatNumber(inUseCount), color: BLUE },
    { label: 'Maintenance', value: formatNumber(maintenanceCount), color: ORANGE },
  ];

  stats.forEach((stat, i) => {
    const x = margin + i * (cardWidth + cardGap);

    // Card bg
    doc.setFillColor(245, 245, 250);
    doc.roundedRect(x, statsY, cardWidth, cardHeight, 2, 2, 'F');

    // Label
    doc.setTextColor(...TEXT_SECONDARY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 4, statsY + 7);

    // Value
    doc.setTextColor(...stat.color);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + 4, statsY + 14);
  });

  // ========================
  // TABLE
  // ========================
  const tableColumns = [
    { header: 'Name', dataKey: 'name' },
    { header: 'Code / Serial', dataKey: 'code' },
    { header: 'Category', dataKey: 'category' },
    { header: 'Status', dataKey: 'status' },
    { header: 'Location', dataKey: 'location' },
  ];

  const tableRows = equipment.map((e) => ({
    name: e.name,
    code: e.assetCode || e.serialNumber || '-',
    category: getCategoryName(e.categoryId),
    status: getStatusLabel(e.status),
    location: e.warehouse?.name || '-',
    _status: e.status,
  }));

  autoTable(doc, {
    startY: statsY + cardHeight + 8,
    columns: tableColumns,
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left',
    },
    columnStyles: {
      status: { halign: 'center' },
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const row = tableRows[data.row.index];
        if (!row) return;

        // Row background for maintenance
        if (row._status === EquipmentStatus.MAINTENANCE) {
          data.cell.styles.fillColor = MAINTENANCE_BG;
        }

        // Status column color coding
        if (data.column.dataKey === 'status') {
          switch (row._status) {
            case EquipmentStatus.AVAILABLE:
              data.cell.styles.textColor = GREEN;
              break;
            case EquipmentStatus.IN_USE:
              data.cell.styles.textColor = BLUE;
              break;
            case EquipmentStatus.MAINTENANCE:
              data.cell.styles.textColor = ORANGE;
              break;
            case EquipmentStatus.RESERVED:
              data.cell.styles.textColor = PURPLE;
              break;
            case EquipmentStatus.RETIRED:
              data.cell.styles.textColor = RED;
              break;
          }
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    didDrawPage: () => {
      const pageNumber = doc.getCurrentPageInfo().pageNumber;

      // Footer line
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

      // Footer text
      doc.setFontSize(7);
      doc.setTextColor(...TEXT_SECONDARY);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${today}`, margin, pageHeight - 7);
      doc.text(
        `Page ${pageNumber} of {total_pages}`,
        pageWidth - margin,
        pageHeight - 7,
        { align: 'right' },
      );
    },
  });

  // Replace total pages placeholder
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.putTotalPages('{total_pages}');
  }

  // Save
  const filename = generateEquipmentAssetsPdfFilename();
  doc.save(filename);
}
